"use client";

import { Box, Typography } from "@mui/material";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

// Read on the section header, so the number cell and the body indent line up
// without a directional padding the rtl transform would mirror.
const NUMBER_COLUMN = "24px";

const LEAD = "۴۷ سال اعتبار را با کالای غیر اصل به خطر نمی‌اندازیم.";
const BODY =
    "عینک وحدت از فروشگاه‌های منتخب Luxottica است و سالانه دو بار برای انتخاب و سفارش اختصاصی مجموعه‌ها به دفتر Luxottica در دبی دعوت می‌شود. برای ما اصالت فقط عبارتی کنار محصول نیست؛ از انتخاب و سفارش کالا شروع می‌شود و تا نگهداری، ارائه، ساخت عدسی و خدمات بعد از خرید ادامه دارد.";

const PROOF_POINTS = [
    "۴۷ سال سابقه",
    "ضمانت اصالت کالا",
    "تنظیم و سرویس رایگان",
    "۳۵ سال تجربه استادکاران",
    "اندازه‌گیری ZEISS RVT",
    "تراش با تجهیزات Essilor",
];

// The claim is what the shopper reads closed, the title names the reason and the
// kicker is the one line worth keeping after the body. Nothing here is brand
// specific: the same five reasons run on every brand page.
const REASONS = [
    {
        n: "۰۱",
        title: "احترام به مشتری",
        claim: "فروش به هر قیمت، هدف ما نیست.",
        body: "اگر عینکی به صورت شما نیاید یا انتخاب مناسبی برایتان نباشد، به شما می‌گوییم انتخابش نکنید. تلاش ما این است که در تمام مراحل — از انتخاب فریم و شناخت محصول تا انتخاب عدسی و خدمات بعدی — اطلاعاتی را که برای تصمیم‌گیری نیاز دارید در اختیارتان بگذاریم.",
        kicker: "انتخاب آگاهانه برای ما مهم‌تر از تمام کردن یک فروش است.",
    },
    {
        n: "۰۲",
        title: "خدمت بعد از خرید ادامه دارد",
        claim: "خرید عینک، پایان رابطه ما با شما نیست.",
        body: "اگر عینکتان حتی دو سال بعد از خرید کج شده یا از تنظیم خارج شده باشد، می‌توانید دوباره به وحدت مراجعه کنید. تا زمانی که قطعه‌ای نشکسته و نیاز به تعویض نداشته باشد، تنظیم و سرویس عینک برای شما رایگان است. اگر عینک آسیب دیده باشد، تا جایی که امکان داشته باشد آن را تعمیر می‌کنیم؛ و اگر قطعه‌ای نیاز به تعویض داشته باشد و هنوز جزو تولیدات کارخانه باشد، امکان سفارش آن را بررسی می‌کنیم.",
        kicker: "تنظیم و سرویس رایگان؛ حتی سال‌ها بعد از خرید.",
    },
    {
        n: "۰۳",
        title: "از اپتومتری تا ساخت عینک، داخل وحدت",
        claim: "اگر عینک طبی می‌خواهید، فریم فقط نیمی از انتخاب شماست.",
        body: "برای تعیین نمره می‌توانید از خدمات اپتومتری مجموعه استفاده کنید. انتخاب عدسی به یک برند محدود نیست؛ از ZEISS، HOYA و Rodenstock تا گزینه‌های اقتصادی‌تر. اندازه‌گیری فریم برای عدسی سفارشی با دستگاه ZEISS RVT انجام می‌شود و تراش و ساخت عدسی داخل وحدت، با دستگاه‌های Essilor و توسط استادکارانی با بیش از ۳۵ سال تجربه.",
        kicker: "از انتخاب تا ساخت، مسئولیت کار را به مجموعه دیگری واگذار نمی‌کنیم.",
    },
    {
        n: "۰۴",
        title: "احترام به کالا",
        claim: "عینکی که قرار است روی صورت شما بنشیند، فقط یک کالا نیست.",
        body: "از زمان ورود کالا به وحدت تا لحظه‌ای که روی صورت شما تنظیم می‌شود، باید با دقت از آن مراقبت شود. نوع نگهداری، ارائه، تنظیم و کار روی فریم، بخشی از همان احترامی است که باید به کالا و در نهایت به شما گذاشته شود.",
        kicker: "مراقبت از عینک، پیش از خرید شما شروع می‌شود.",
    },
    {
        n: "۰۵",
        title: "تنوعی که برای انتخاب ساخته شده",
        claim: "وقتی سراغ یک برند می‌آیید، باید حق انتخاب داشته باشید.",
        body: "یکی از بازخوردهایی که بارها از مشتریانی می‌شنویم که پیش از وحدت فروشگاه‌های دیگری را هم دیده‌اند، تنوع چشمگیر مدل‌های موجود است. هدف ما این نیست که شما را از میان چند گزینه محدود به انتخاب برسانیم.",
        kicker: "مدل‌های بیشتر، انتخاب واقعی‌تر.",
    },
];

export default function BrandWhyVahdat() {
    return (
        <Box component="section" sx={{
            bgcolor: "#16161a",
            color: "#fff",
            // Starts on the exact tone the video playlist ends on, so the two dark
            // blocks read as one region instead of a seam, then lifts behind the cards
            // and settles again before the light section below. Both layers are centred
            // or vertical, so the rtl transform cannot mirror them.
            backgroundImage: "radial-gradient(120% 88% at 50% 0%, rgba(187,32,46,.22), rgba(187,32,46,0) 60%), linear-gradient(180deg, #16161a 0%, #26292e 46%, #17191c 100%)",
        }}>
            <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, lg2: 0 }, py: { xs: 5, sm2: 7, lg: 8 } }}>
                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1.15fr 1fr" },
                    columnGap: { lg: 8 },
                    rowGap: { xs: 4, sm2: 5 },
                    alignItems: "start",
                }}>
                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                            <Typography sx={{ flexShrink: 0, color: "primary.light", fontSize: 13, fontWeight: 700, letterSpacing: ".14em" }}>
                                چرا عینک وحدت؟
                            </Typography>
                            <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(255,255,255,.16)" }} />
                        </Box>

                        <Typography component="h2" sx={{ m: 0, maxWidth: 620, fontSize: { xs: 22, sm2: 26, lg: 30 }, fontWeight: 700, lineHeight: 1.4, letterSpacing: "-.02em" }}>
                            «اصالت»؛ اصلی که بر دیده نهادیم
                        </Typography>

                        <Typography sx={{ mt: 2.5, maxWidth: 520, fontSize: { xs: 16, sm2: 17, lg: 19 }, fontWeight: 700, lineHeight: 1.7 }}>
                            {LEAD}
                        </Typography>

                        <Typography sx={{ mt: 2, maxWidth: 540, color: "rgba(255,255,255,.68)", fontSize: { xs: 14, sm2: 15 }, lineHeight: 2.05, textAlign: "justify" }}>
                            {BODY}
                        </Typography>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mt: 3.5, maxWidth: 540 }}>
                            {PROOF_POINTS.map((point) => (
                                <Typography key={point} component="span" sx={{
                                    border: "1px solid rgba(255,255,255,.22)",
                                    color: "rgba(255,255,255,.86)",
                                    borderRadius: 99,
                                    px: 1.6,
                                    py: 1,
                                    fontSize: 12,
                                    fontWeight: 600,
                                }}>
                                    {point}
                                </Typography>
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{pt: 1}}>
                        {/* <Typography sx={{ mb: 0.5, color: "rgba(255,255,255,.5)", fontSize: 12, fontWeight: 600, letterSpacing: ".16em" }}>
                            پنج دلیل
                        </Typography> */}

                        {REASONS.map((reason) => (
                            <Reason key={reason.n} {...reason} />
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

function Reason({ n, title, claim, body, kicker }: (typeof REASONS)[number]) {
    const [open, setOpen] = useState(false);

    return (
        <Box sx={{ borderTop: "1px solid rgba(255,255,255,.16)" }}>
            <Box
                component="button"
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
                sx={{
                    display: "grid",
                    gridTemplateColumns: `${NUMBER_COLUMN} 1fr auto`,
                    gap: 1.75,
                    alignItems: "start",
                    width: 1,
                    py: 2.4,
                    px: 0,
                    border: 0,
                    bgcolor: "transparent",
                    color: "inherit",
                    font: "inherit",
                    textAlign: "start",
                    cursor: "pointer",
                }}
            >
                <Typography component="span" sx={{ pt: 0.5, color: "primary.light", fontSize: 12, fontWeight: 700 }}>
                    {n}
                </Typography>
                <Box>
                    {/* The claim is the heading a shopper scans; the reason name sits under
                        it as a label rather than a second heading. */}
                    <Typography component="h3" sx={{ m: 0, fontSize: 15, fontWeight: 700, lineHeight: 1.55 }}>
                        {claim}
                    </Typography>
                    <Typography sx={{ mt: 0.6, color: "rgba(255,255,255,.45)", fontSize: 12 }}>
                        {title}
                    </Typography>
                </Box>
                <Box sx={{ pt: 0.4, display: "flex", transform: open ? "rotate(180deg)" : "none", transition: "150ms" }}>
                    <ChevronDown size={18} color="rgba(255,255,255,.5)" />
                </Box>
            </Box>

            {open && (
                <Box sx={{ display: "grid", gridTemplateColumns: `${NUMBER_COLUMN} 1fr`, gap: 1.75, pb: 2 }}>
                    <Box />
                    <Box>
                        <Typography sx={{ color: "rgba(255,255,255,.66)", fontSize: 13, lineHeight: 2.05, textAlign: "justify" }}>
                            {body}
                        </Typography>
                        <Typography sx={{ mt: 1.25, fontSize: 13, fontWeight: 700, lineHeight: 1.8 }}>
                            {kicker}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
